import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Shuffle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Generate random avatar seeds for selection
const generateAvatarSeeds = () => {
  const seeds = [];
  for (let i = 0; i < 12; i++) {
    seeds.push(`avatar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  }
  return seeds;
};

interface PixelAvatarSelectorProps {
  currentAvatar: string;
  onAvatarChange: (avatarSeed: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function PixelAvatarSelector({ 
  currentAvatar, 
  onAvatarChange, 
  isOpen, 
  onClose 
}: PixelAvatarSelectorProps) {
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const [avatarSeeds, setAvatarSeeds] = useState(() => generateAvatarSeeds());

  // V4 FIX: Sync selectedAvatar with currentAvatar when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAvatar(currentAvatar);
    }
  }, [isOpen, currentAvatar]);

  const handleSave = () => {
    onAvatarChange(selectedAvatar);
    onClose();
  };

  const regenerateAvatars = () => {
    setAvatarSeeds(generateAvatarSeeds());
  };

  const randomizeAvatar = () => {
    const randomSeed = `avatar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSelectedAvatar(randomSeed);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choose Your Avatar</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Current Selection */}
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-2">
              <img 
                src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${selectedAvatar}`}
                alt="Selected avatar"
                className="w-full h-full rounded-full border-4 border-primary shadow-glow"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={randomizeAvatar}
              className="gap-2"
            >
              <Shuffle className="w-4 h-4" />
              Randomize
            </Button>
          </div>

          {/* Avatar Grid */}
          <div className="grid grid-cols-4 gap-3">
            {avatarSeeds.map((seed) => (
              <Card 
                key={seed}
                className={`p-2 cursor-pointer transition-all hover:scale-105 ${
                  selectedAvatar === seed 
                    ? 'ring-2 ring-primary shadow-glow' 
                    : 'hover:shadow-card'
                }`}
                onClick={() => setSelectedAvatar(seed)}
              >
                <div className="relative">
                  <img 
                    src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${seed}`}
                    alt="Avatar option"
                    className="w-full aspect-square rounded-lg"
                  />
                  {selectedAvatar === seed && (
                    <div className="absolute inset-0 bg-primary/20 rounded-lg flex items-center justify-center">
                      <div className="bg-primary rounded-full p-1">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Regenerate Button */}
          <div className="text-center">
            <Button
              variant="outline"
              onClick={regenerateAvatars}
              className="gap-2"
            >
              <Shuffle className="w-4 h-4" />
              More Options
            </Button>
          </div>
        </div>
        
        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1">
            Save Avatar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}